import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Stack, Typography } from '@mui/material';

import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { FormTextField } from 'components/common/form/Form';
import { AddressField } from 'components/common/form/AddressField';
import { RecipientField } from 'components/shared/sharing/RecipientField';
import { PeopleWithAccess } from 'components/shared/sharing/PeopleWithAccess';

import { createPropertySchema } from 'validation/form/property';
import { listStates, selectProperty } from 'store/slices/properties';
import { useProperty } from 'hooks/useProperty';
import { useShareProperty } from 'hooks/useShareProperty';
import { useRoutePath } from 'hooks/useRoutePath';

export const NewProperty = (props) => {
  const { close } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { handleCreateProperty } = useProperty();
  const { parentPath } = useRoutePath();

  const statuses = useSelector((state) => state.properties.statuses.value);
  // const types = useSelector((state) => state.properties.types.value);
  // const salesMethods = useSelector((state) => state.properties.salesMethods.value);
  // const organizations = useSelector((state) => state.properties.organizations.value);
  const loading = useSelector((state) => state.properties.newPropertyForm.loading);
  const currentUser = useSelector((state) => state.auth.currentUser);

  const { userList, usersLoading, handleSearchUser } = useShareProperty(null, currentUser);

  const defaultValues = useMemo(() => {
    return {
      address: {
        address: '',
        suburb: '',
        state: '',
        postcode: '',
        place_id: '',
      },
      sales_method: '',
      organisation_id: '',
      contacts: [],
      status: 'New',
      property_type: '',
      bedrooms: '',
      bathrooms: '',
      carspaces: '',
      vendor_contact: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
      },
      user_name: currentUser?.name || '',
      shares: [],
    };
  }, [currentUser]);

  const methods = useForm({
    resolver: yupResolver(createPropertySchema),
    defaultValues,
  });

  const handleSave = methods.handleSubmit((data) => {
    const parentId = parentPath;

    const payload = {
      ...data,
      shares:
        data.shares?.map((user) => {
          const accessArray = user.accesses === 'Write' ? ['Read', 'Write'] : ['Read'];

          return {
            account_id: user.id,
            accesses: accessArray,
          };
        }) || [],
    };

    handleCreateProperty(payload, parentId, (response) => {
      if (response?.id) {
        dispatch(selectProperty(response));
        navigate(`/properties/${response.id}`);
      }
      close();
    });
  });

  const handleShare = (user) => {
    const currentShares = methods.getValues('shares') || [];

    const alreadyShared = currentShares.some((share) => share.id === user.id);
    if (alreadyShared) return;

    methods.setValue('shares', [...currentShares, { ...user, accesses: 'Write' }]);
  };

  const handleShareChange = (user, newStatus) => {
    const currentShares = methods.getValues('shares') || [];

    const checkStatus = newStatus.includes('Write') ? 'Write' : 'Read';

    if (checkStatus === 'Remove') {
      methods.setValue(
        'shares',
        currentShares.filter((share) => share.id !== user.id)
      );
      return;
    } else {
      const updatedShares = currentShares.map((share) =>
        share.id === user.id ? { ...share, accesses: checkStatus } : share
      );
      methods.setValue('shares', updatedShares);
    }
  };

  const handleDefaultStatus = useCallback(() => {
    const newStatus = statuses.find((status) => status.value === 'New');
    methods.reset({
      ...defaultValues,
      status: newStatus?.value ?? '',
    });
  }, [statuses, methods, defaultValues]);

  const handleFetchPropertyEnums = useCallback(() => {
    return Promise.all([
      dispatch(listStates()),
      // dispatch(listStatuses()),
      // dispatch(listTypes()),
      // dispatch(listSalesMethods()),
      // dispatch(listOrganizations()),
    ]);
  }, [dispatch]);

  useEffect(() => {
    handleFetchPropertyEnums();
  }, [handleFetchPropertyEnums]);

  useEffect(() => {
    if (statuses.length > 0) {
      handleDefaultStatus();
    }
  }, [statuses, handleDefaultStatus]);

  const shares = methods.watch('shares') || [];

  return (
    <DrawerContent>
      <DrawerContent.Header title="New Property" />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <FormProvider {...methods}>
            <AddressField
              currentAddress={{ value: methods.watch('address') }}
              callbacks={{
                toggleSetValue: (updatedAddress) => {
                  methods.setValue(
                    'address',
                    { ...updatedAddress, place_id: updatedAddress?.place_id ?? '' },
                    { shouldValidate: true }
                  );
                },
              }}
            />
            <Typography variant="subtitle2">Extra Details</Typography>
            <FormTextField
              name="bedrooms"
              label="Bed"
              control={methods.control}
              type="number"
            />
            <FormTextField
              name="bathrooms"
              label="Bath"
              control={methods.control}
              type="number"
            />
            <FormTextField
              name="carspaces"
              label="Car"
              control={methods.control}
              type="number"
            />
            <FormTextField
              name="vendor_contact.first_name"
              label="Vendor First Name"
              control={methods.control}
            />
            <FormTextField
              name="vendor_contact.last_name"
              label="Vendor Last Name"
              control={methods.control}
            />
            <FormTextField
              name="vendor_contact.phone"
              label="Vendor Phone Number"
              control={methods.control}
            />
            <FormTextField
              name="vendor_contact.email"
              label="Vendor Email"
              control={methods.control}
            />
            {/* <FormSelectField
              name="status"
              label="Status"
              control={methods.control}
              options={statuses}
              valueName="value"
              optionName="name"
            /> */}
            {/* <FormSelectField
              name="organisation_id"
              label="Organisation"
              control={methods.control}
              options={organizations}
              valueName="id"
              optionName="name"
            /> */}
            {/* <FormSelectField
              name="sales_method"
              label="Sales Method"
              control={methods.control}
              options={salesMethods}
              valueName="value"
              optionName="name"
            /> */}
            {/* <FormSelectField
              name="property_type"
              label="Type"
              control={methods.control}
              options={types}
              valueName="value"
              optionName="name"
            /> */}
            <Stack
              pt={1.5}
              spacing={1.5}
            >
              <Typography variant="subtitle2">Share</Typography>
              <RecipientField
                optionsData={userList}
                backendSearch={handleSearchUser}
                loading={usersLoading}
                handleShare={handleShare}
              />
              <PeopleWithAccess>
                <PeopleWithAccess.Account>
                  <PeopleWithAccess.AccountName accountName={currentUser.name} />
                  <PeopleWithAccess.AccountAccess accountAccess="Owner" />
                </PeopleWithAccess.Account>
                {shares.map((user, index) => (
                  <PeopleWithAccess.Account key={`${user.id}-${index}`}>
                    <PeopleWithAccess.AccountName accountName={user.name} />
                    <PeopleWithAccess.AccessOptions
                      value={user?.accesses || 'Write'}
                      onChange={(access) => handleShareChange(user, access)}
                    />
                  </PeopleWithAccess.Account>
                ))}
              </PeopleWithAccess>
            </Stack>
          </FormProvider>
        </DrawerContent.Content>
        <DrawerContent.ActionButtons
          submitButtonText="Create"
          submitButtonLoading={loading}
          submit={handleSave}
          close={close}
        />
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
